import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { uploadSmart } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { isImpersonating, endImpersonation } from "@/lib/impersonate";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, Search, Printer, FileText, Paperclip, Camera, Trash2, X, Loader2, ExternalLink, Plus, Undo2, Download, Eye, CheckCircle2 } from "lucide-react";
import { BASKI_STAGE_COLORS, stageBadgeCls } from "@/components/SetupDashboard";

const trFold = (s) => (s || "").toLocaleLowerCase("tr-TR");
const sizeFmt = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const dtFmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const ZONES = [
  ["Green Zone", "Green Zone", "text-emerald-600 border-emerald-600"],
  ["Blue Zone", "Blue Zone", "text-blue-600 border-blue-600"],
];

const FileBadge = ({ ev, fid }) => {
  if (ev?.downloaded) {
    return (
      <span className="relative group/badge shrink-0" data-testid={`taseron-badge-downloaded-${fid}`}>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-1.5 py-0.5 cursor-default"><CheckCircle2 size={10} /> İndirildi</span>
        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap bg-slate-900 text-white text-[10px] font-semibold px-2 py-1 opacity-0 group-hover/badge:opacity-100 transition-opacity z-30">İndirme: {dtFmt(ev.downloaded)}</span>
      </span>
    );
  }
  if (ev?.opened) {
    return (
      <span className="relative group/badge shrink-0" data-testid={`taseron-badge-opened-${fid}`}>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-500/10 text-amber-700 border border-amber-500/30 px-1.5 py-0.5 cursor-default"><Eye size={10} /> Açıldı</span>
        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 whitespace-nowrap bg-slate-900 text-white text-[10px] font-semibold px-2 py-1 opacity-0 group-hover/badge:opacity-100 transition-opacity z-30">Açılma: {dtFmt(ev.opened)}</span>
      </span>
    );
  }
  return null;
};

const AttachUploader = ({ accept, label, icon: Icon, onDone, testid }) => {
  const inputRef = useRef(null);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPending((prev) => [...prev, ...files.map((f) => ({ file: f, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }))]);
    e.target.value = "";
  };

  const send = async () => {
    setBusy(true);
    try {
      const uploaded = await uploadSmart(pending.map((p) => p.file), ({ percent }) => setProgress(percent), { compressImages: accept === "image/*" });
      await onDone(uploaded.map((f) => f.id));
      pending.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
      setPending([]);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); setProgress(0); }
  };

  return (
    <div>
      <input ref={inputRef} type="file" multiple accept={accept} className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testid}-pick`} className="inline-flex items-center gap-1.5 text-xs font-bold border border-dashed border-slate-300 hover:border-slate-900 text-slate-600 hover:text-slate-900 px-3 py-2 transition-colors duration-150">
          <Icon size={13} /> {label}
        </button>
        {pending.length > 0 && (
          <Button onClick={send} disabled={busy} data-testid={`${testid}-send`} className="rounded-none h-8 px-4 bg-slate-900 text-white hover:bg-slate-700 text-xs font-bold gap-1.5">
            {busy ? <><Loader2 size={12} className="animate-spin" /> %{progress}</> : <><Plus size={12} /> {pending.length} dosyayı yükle</>}
          </Button>
        )}
      </div>
      {pending.length > 0 && (
        <div className="mt-2 flex gap-2 flex-wrap" data-testid={`${testid}-pending`}>
          {pending.map((p, i) => (
            <div key={i} className="relative group border border-slate-200 bg-slate-50">
              {p.preview ? (
                <img src={p.preview} alt={p.file.name} className="w-16 h-16 object-cover" />
              ) : (
                <div className="w-16 h-16 flex flex-col items-center justify-center px-1 text-slate-400">
                  <FileText size={16} />
                  <span className="text-[8px] truncate max-w-full mt-1">{p.file.name}</span>
                </div>
              )}
              <button type="button" onClick={() => setPending((prev) => prev.filter((_, idx) => idx !== i))} data-testid={`${testid}-remove-${i}`} className="absolute -top-1.5 -right-1.5 bg-slate-900 text-white w-4 h-4 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"><X size={10} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function TaseronPage() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [stages, setStages] = useState([]);
  const [events, setEvents] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [zone, setZone] = useState("Green Zone");
  const [statusTab, setStatusTab] = useState("all");
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [fileFilter, setFileFilter] = useState("all");
  const [detailId, setDetailId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = () => api.get("/taseron/projects").then((r) => {
    setRows(r.data.projects || []); setStages(r.data.stages || []); setEvents(r.data.events || {}); setLoaded(true);
  }).catch((e) => { toast.error(apiError(e)); setLoaded(true); });
  useEffect(() => { load(); }, []);

  const markEvent = (fid, action) => {
    api.post("/taseron/file-events", { file_id: fid, action }).catch(() => {});
    setEvents((prev) => ({ ...prev, [fid]: { ...prev[fid], [action]: new Date().toISOString() } }));
  };

  const previewFile = async (f) => {
    try {
      const { data } = await api.get(`/files/${f.id}/preview-url`);
      setPreview({ file: f, url: data.url });
      markEvent(f.id, "opened");
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadFile = async (f) => {
    try {
      const { data } = await api.get(`/files/${f.id}/preview-url`);
      const a = document.createElement("a");
      a.href = data.url; a.download = f.original_filename || "dosya";
      document.body.appendChild(a); a.click(); a.remove();
      markEvent(f.id, "downloaded");
      toast.success(`"${f.original_filename}" indiriliyor`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const [zipBusy, setZipBusy] = useState(false);
  const downloadZip = async (p) => {
    setZipBusy(true);
    try {
      const res = await api.get(`/taseron/projects/${p.id}/baski-zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${p.name || "proje"} - baski dosyalari.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const at = new Date().toISOString();
      setEvents((prev) => {
        const nx = { ...prev };
        (p.baski_files || []).forEach((f) => { nx[f.id] = { ...nx[f.id], downloaded: at }; });
        return nx;
      });
      toast.success("Tüm baskı dosyaları ZIP olarak indiriliyor");
    } catch (e) { toast.error(apiError(e)); } finally { setZipBusy(false); }
  };

  const stagePct = (p) => {
    const i = stages.indexOf(p.baski_sureci);
    return i >= 0 ? Math.round(((i + 1) / stages.length) * 100) : 0;
  };

  const filtered = useMemo(() => {
    const f = trFold(q);
    return rows.filter((p) => {
      if (p.zone !== zone) return false;
      if (zone === "Green Zone" && statusTab !== "all" && p.project_status !== statusTab) return false;
      if (f && !trFold(p.name).includes(f) && !trFold(p.stand_no).includes(f)) return false;
      if (stageFilter === "none" && p.baski_sureci) return false;
      if (stageFilter !== "all" && stageFilter !== "none" && p.baski_sureci !== stageFilter) return false;
      const files = p.baski_files || [];
      if (fileFilter === "has" && files.length === 0) return false;
      if (fileFilter === "nofiles" && files.length > 0) return false;
      if (fileFilter === "pending" && (files.length === 0 || files.every((x) => events[x.id]?.downloaded))) return false;
      if (fileFilter === "downloaded" && (files.length === 0 || !files.every((x) => events[x.id]?.downloaded))) return false;
      return true;
    });
  }, [rows, zone, q, stageFilter, fileFilter, events, statusTab]);

  const detailP = useMemo(() => rows.find((r) => r.id === detailId) || null, [rows, detailId]);

  const patchRow = (pid, patch) => setRows((prev) => prev.map((p) => (p.id === pid ? { ...p, ...patch } : p)));

  const setStatus = async (p, status) => {
    const old = p.baski_sureci;
    patchRow(p.id, { baski_sureci: status || null });
    try {
      await api.patch(`/taseron/projects/${p.id}/status`, { status });
      toast.success(status ? `Süreç güncellendi: ${status}` : "Süreç temizlendi");
    } catch (e) { patchRow(p.id, { baski_sureci: old }); toast.error(apiError(e)); }
  };

  const addPhotos = async (p, fileIds) => {
    const { data } = await api.post(`/taseron/projects/${p.id}/photos`, { file_ids: fileIds });
    patchRow(p.id, { taseron_photos: [...(p.taseron_photos || []), ...data.photos] });
    toast.success(`${data.photos.length} fotoğraf eklendi`);
  };

  const deletePhoto = async (p, f) => {
    try {
      await api.delete(`/taseron/projects/${p.id}/photos/${f.id}`);
      patchRow(p.id, { taseron_photos: (p.taseron_photos || []).filter((x) => x.id !== f.id) });
      toast.success("Fotoğraf kaldırıldı");
    } catch (e) { toast.error(apiError(e)); }
  };

  const addTeklif = async (p, fileIds) => {
    const { data } = await api.post(`/taseron/projects/${p.id}/teklif`, { file_ids: fileIds });
    patchRow(p.id, { teklif_files: [...(p.teklif_files || []), ...data.files] });
    toast.success("Teklif dosyası eklendi");
  };

  const deleteTeklif = async (p, f) => {
    try {
      await api.delete(`/taseron/projects/${p.id}/teklif/${f.id}`);
      patchRow(p.id, { teklif_files: (p.teklif_files || []).filter((x) => x.id !== f.id) });
      toast.success("Teklif dosyası kaldırıldı");
    } catch (e) { toast.error(apiError(e)); }
  };

  const isImg = (f) => (f?.content_type || "").startsWith("image/");
  const isPdf = (f) => (f?.content_type || "") === "application/pdf";

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="taseron-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><Printer size={10} /> Baskı Portalı</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:block" data-testid="taseron-user-name">{user?.contact_name}</span>
            {isImpersonating() && (
              <button onClick={endImpersonation} data-testid="taseron-end-impersonation" className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors duration-200">
                <Undo2 size={14} /> Yönetici Hesabına Dön
              </button>
            )}
            <Button variant="outline" onClick={logout} data-testid="taseron-logout" className="rounded-none h-9 border-slate-300 gap-1.5 text-xs font-semibold">
              <LogOut size={13} /> Çıkış
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Projeler</h1>
        <p className="text-sm text-slate-500 mt-1">{loaded ? `${filtered.length} proje — baskı dosyaları, üretim süreci ve teklifler` : "Yükleniyor..."}</p>

        {(() => {
          const zrows = rows.filter((p) => p.zone === zone && (zone !== "Green Zone" || statusTab === "all" || p.project_status === statusTab));
          const allFiles = zrows.flatMap((p) => p.baski_files || []);
          const dl = allFiles.filter((f) => events[f.id]?.downloaded).length;
          const withFiles = zrows.filter((p) => (p.baski_files || []).length > 0).length;
          const inProc = zrows.filter((p) => p.baski_sureci).length;
          const done = zrows.filter((p) => p.baski_sureci === "Faturası kesildi").length;
          const m2 = zrows.reduce((s, p) => s + (parseFloat(String(p.area_m2 || "0").replace(",", ".")) || 0), 0);
          return (
            <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="taseron-kpis">
              <div className="bg-white border border-slate-200 p-4" data-testid="taseron-kpi-projects">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Proje</div>
                <div className="font-heading text-2xl font-extrabold tabular-nums mt-1">{zrows.length}</div>
                <div className="text-[11px] text-slate-500 tabular-nums mt-0.5">{m2.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} m² toplam</div>
              </div>
              <div className="bg-white border border-slate-200 p-4" data-testid="taseron-kpi-files">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Baskı Dosyası</div>
                <div className="font-heading text-2xl font-extrabold tabular-nums mt-1">{allFiles.length}</div>
                <div className="text-[11px] text-slate-500 tabular-nums mt-0.5">{withFiles} projede · {zrows.length - withFiles} projede yok</div>
              </div>
              <div className="bg-white border border-slate-200 p-4" data-testid="taseron-kpi-downloads">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">İndirilen Dosya</div>
                <div className="font-heading text-2xl font-extrabold tabular-nums mt-1">{dl}<span className="text-sm font-semibold text-slate-400">/{allFiles.length}</span></div>
                <div className={`text-[11px] tabular-nums mt-0.5 ${allFiles.length === 0 ? "text-slate-400" : allFiles.length - dl > 0 ? "text-red-500 font-semibold" : "text-emerald-600 font-semibold"}`}>{allFiles.length === 0 ? "Dosya yok" : allFiles.length - dl > 0 ? `${allFiles.length - dl} dosya indirilmedi` : "Tümü indirildi"}</div>
                <div className="mt-2 h-1.5 bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-400" style={{ width: `${allFiles.length ? Math.round((dl / allFiles.length) * 100) : 0}%` }} /></div>
              </div>
              <div className="bg-white border border-slate-200 p-4" data-testid="taseron-kpi-process">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Üretim Süreci</div>
                <div className="font-heading text-2xl font-extrabold tabular-nums mt-1">{inProc}<span className="text-sm font-semibold text-slate-400">/{zrows.length}</span></div>
                <div className="text-[11px] text-slate-500 tabular-nums mt-0.5"><span className="text-emerald-600 font-semibold">{done} faturası kesildi</span> · {zrows.length - inProc} başlamadı</div>
              </div>
            </div>
          );
        })()}

        <div className="mt-5 flex border border-slate-200 w-fit bg-white">
          {ZONES.map(([z, lbl, cls]) => (
            <button key={z} type="button" onClick={() => { setZone(z); setStatusTab("all"); }} data-testid={`taseron-zone-${z === "Green Zone" ? "green" : "blue"}`} className={`px-5 py-2.5 text-xs font-bold border-b-2 transition-colors duration-150 ${zone === z ? cls : "border-transparent text-slate-400 hover:text-slate-700"}`}>
              {lbl} ({rows.filter((p) => p.zone === z).length})
            </button>
          ))}
        </div>
        {zone === "Green Zone" && (
          <div className="mt-3 flex border border-slate-200 w-fit bg-white" data-testid="taseron-status-tabs">
            {[["all", "Tümü"], ["Özel", "Özel"], ["SBB", "SBB"]].map(([k, lbl]) => (
              <button key={k} type="button" onClick={() => setStatusTab(k)} data-testid={`taseron-status-tab-${k === "all" ? "all" : k.toLowerCase()}`} className={`px-4 py-2 text-xs font-bold border-b-2 transition-colors duration-150 ${statusTab === k ? "text-slate-900 border-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
                {lbl} ({rows.filter((p) => p.zone === "Green Zone" && (k === "all" || p.project_status === k)).length})
              </button>
            ))}
          </div>
        )}

        <div className="mt-4 mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative sm:max-w-xs flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje veya stand ara..." data-testid="taseron-search" className="rounded-none h-10 pl-9 border-slate-200 bg-white" />
          </div>
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="taseron-filter-stage" className="h-10 border border-slate-200 bg-white px-3 text-xs font-semibold sm:w-[200px]">
            <option value="all">Süreç: Tümü</option>
            <option value="none">Süreç başlamadı</option>
            {stages.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fileFilter} onChange={(e) => setFileFilter(e.target.value)} data-testid="taseron-filter-file" className="h-10 border border-slate-200 bg-white px-3 text-xs font-semibold sm:w-[230px]">
            <option value="all">Dosya: Tümü</option>
            <option value="has">Baskı dosyası olan</option>
            <option value="nofiles">Baskı dosyası olmayan</option>
            <option value="pending">İndirilmemiş dosyası olan</option>
            <option value="downloaded">Tüm dosyaları indirilen</option>
          </select>
        </div>

        {!loaded && <div className="flex justify-center py-16 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="taseron-grid">
          {filtered.map((p) => {
            const files = p.baski_files || [];
            const dlCount = files.filter((f) => events[f.id]?.downloaded).length;
            const pct = stagePct(p);
            return (
              <button type="button" key={p.id} onClick={() => setDetailId(p.id)} data-testid={`taseron-card-${p.id}`} className="bg-white border border-slate-200 hover:border-slate-900 p-5 text-left transition-colors duration-150">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-heading text-sm font-extrabold leading-tight">{p.name}</div>
                  {p.baski_sureci && <span className={`shrink-0 text-[10px] font-bold border px-1.5 py-0.5 ${stageBadgeCls(p.baski_sureci)}`}>{p.baski_sureci}</span>}
                </div>
                <div className="text-[11px] text-slate-400 mt-1">{p.stand_no || "—"}{p.hub ? ` · ${p.hub}` : ""}{p.project_status ? ` · ${p.project_status}` : ""} · {p.area_m2 || "—"} m²</div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-100 overflow-hidden">
                    <div className={`h-full ${BASKI_STAGE_COLORS[p.baski_sureci]?.bar || (pct > 0 ? "bg-blue-400" : "")}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className={`text-[10px] font-bold tabular-nums ${pct === 100 ? "text-emerald-600" : "text-slate-500"}`}>%{pct}</span>
                </div>
                <div className="mt-2.5 flex items-center gap-2 flex-wrap text-[10px]">
                  <span className={`font-bold px-1.5 py-0.5 border ${files.length ? "bg-sky-500/10 text-sky-700 border-sky-500/20" : "text-slate-400 border-slate-200"}`}>{files.length} baskı dosyası</span>
                  {files.length > 0 && (
                    <span className={`font-bold px-1.5 py-0.5 border ${dlCount === files.length ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : dlCount > 0 ? "bg-amber-500/10 text-amber-700 border-amber-500/30" : "bg-red-500/10 text-red-600 border-red-500/20"}`}>
                      {dlCount}/{files.length} indirildi
                    </span>
                  )}
                  {(p.taseron_photos || []).length > 0 && <span className="text-slate-400 font-semibold">{p.taseron_photos.length} foto</span>}
                  {(p.teklif_files || []).length > 0 && <span className="text-slate-400 font-semibold">{p.teklif_files.length} teklif</span>}
                </div>
              </button>
            );
          })}
        </div>
        {loaded && filtered.length === 0 && (
          <div className="bg-white border border-slate-200 px-6 py-14 text-center text-slate-400" data-testid="taseron-empty">Filtrelere uyan proje bulunamadı.</div>
        )}
      </main>

      {detailP && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setDetailId(null)} data-testid="taseron-detail-modal">
          <div className="bg-white w-full max-w-[640px] max-h-[90vh] overflow-y-auto border border-slate-200" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-3">
              <div>
                <div className="font-heading text-lg font-extrabold leading-tight">{detailP.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">{detailP.stand_no || "—"} · {detailP.zone}{detailP.hub ? ` · ${detailP.hub}` : ""} · {detailP.area_m2 || "—"} m²</div>
              </div>
              <button type="button" onClick={() => setDetailId(null)} data-testid="taseron-detail-close" className="text-slate-400 hover:text-slate-900 p-1"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Baskı Dosyaları</div>
                  {(detailP.baski_files || []).length > 0 && (
                    <button type="button" onClick={() => downloadZip(detailP)} disabled={zipBusy} data-testid="taseron-zip-download" className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-slate-900 text-white px-3 py-1.5 hover:bg-slate-700 disabled:opacity-60 transition-colors duration-150">
                      {zipBusy ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Tümünü ZIP İndir ({detailP.baski_files.length})
                    </button>
                  )}
                </div>
                {(detailP.baski_files || []).length === 0 ? (
                  <div className="text-xs text-slate-400">Bu projede baskı dosyası yok.</div>
                ) : (
                  <ul className="divide-y divide-slate-100 border border-slate-100">
                    {detailP.baski_files.map((f) => {
                      const ev = events[f.id];
                      return (
                        <li key={f.id} data-testid={`taseron-file-row-${f.id}`} className={`px-3 py-2.5 flex items-center gap-2 text-xs ${ev?.downloaded ? "bg-emerald-50/60" : ev?.opened ? "bg-amber-50/60" : ""}`}>
                          <FileText size={13} className="text-sky-500 shrink-0" />
                          <span className="font-semibold truncate flex-1">{f.original_filename}</span>
                          <FileBadge ev={ev} fid={f.id} />
                          <span className="text-slate-400 shrink-0">{sizeFmt(f.size || 0)}</span>
                          <button type="button" onClick={() => previewFile(f)} data-testid={`taseron-file-preview-${f.id}`} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-bold shrink-0"><Eye size={12} /> Önizle</button>
                          <button type="button" onClick={() => downloadFile(f)} data-testid={`taseron-file-download-${f.id}`} className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-800 font-bold shrink-0"><Download size={12} /> İndir</button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Üretim Süreci</div>
                <select value={detailP.baski_sureci || ""} onChange={(e) => setStatus(detailP, e.target.value)} data-testid={`taseron-status-${detailP.id}`} className={`h-10 border px-3 text-sm font-semibold w-full sm:w-[280px] ${detailP.baski_sureci ? stageBadgeCls(detailP.baski_sureci) : "border-slate-200 bg-white"}`}>
                  <option value="">— Süreç seçin —</option>
                  {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="mt-2 flex items-center gap-2 sm:w-[280px]">
                  <div className="flex-1 h-1.5 bg-slate-100 overflow-hidden">
                    <div className={`h-full ${BASKI_STAGE_COLORS[detailP.baski_sureci]?.bar || "bg-blue-400"}`} style={{ width: `${stagePct(detailP)}%` }} />
                  </div>
                  <span className="text-[10px] font-bold tabular-nums text-slate-500">%{stagePct(detailP)}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1" data-testid="taseron-stage-legend">
                  {stages.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 text-[9px] font-semibold text-slate-500">
                      <span className={`inline-block w-2 h-2 ${BASKI_STAGE_COLORS[s]?.dot || "bg-slate-300"}`} /> {s}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Üretilen Baskı Fotoğrafları</div>
                {(detailP.taseron_photos || []).length > 0 && (
                  <div className="flex gap-2 flex-wrap mb-3" data-testid={`taseron-photos-${detailP.id}`}>
                    {detailP.taseron_photos.map((f) => (
                      <div key={f.id} className="relative group">
                        <img src={`${f.url}?thumb=1`} alt={f.original_filename} loading="lazy" onClick={() => setLightbox(f)} className="w-24 h-24 object-cover border border-slate-200 cursor-zoom-in hover:opacity-90 transition-opacity" />
                        {f.uploaded_by === user?.id && (
                          <button type="button" onClick={() => deletePhoto(detailP, f)} data-testid={`taseron-photo-delete-${f.id}`} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white w-5 h-5 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"><Trash2 size={11} /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <AttachUploader accept="image/*" label="Fotoğraf ekle (birden fazla seçilebilir)" icon={Camera} onDone={(ids) => addPhotos(detailP, ids)} testid={`taseron-photo-upload-${detailP.id}`} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Teklifler</div>
                {(detailP.teklif_files || []).length > 0 && (
                  <ul className="divide-y divide-slate-100 border border-slate-100 mb-3" data-testid={`taseron-teklif-list-${detailP.id}`}>
                    {detailP.teklif_files.map((f) => (
                      <li key={f.id} className="px-3 py-2 flex items-center gap-2 text-xs">
                        <Paperclip size={13} className="text-amber-500 shrink-0" />
                        <span className="font-semibold truncate flex-1">{f.original_filename}</span>
                        <span className="text-slate-400 shrink-0">{sizeFmt(f.size || 0)}</span>
                        <button type="button" onClick={() => previewFile(f)} data-testid={`taseron-teklif-open-${f.id}`} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-bold shrink-0"><ExternalLink size={12} /> Aç</button>
                        {f.uploaded_by === user?.id && (
                          <button type="button" onClick={() => deleteTeklif(detailP, f)} data-testid={`taseron-teklif-delete-${f.id}`} className="text-slate-400 hover:text-red-500 shrink-0"><Trash2 size={13} /></button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <AttachUploader accept="" label="Teklif dosyası ekle" icon={Paperclip} onDone={(ids) => addTeklif(detailP, ids)} testid={`taseron-teklif-upload-${detailP.id}`} />
              </div>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="taseron-preview-modal">
          <div className="bg-white w-full max-w-[860px] max-h-[92vh] flex flex-col border border-slate-200" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
              <FileText size={15} className="text-sky-500 shrink-0" />
              <span className="font-semibold text-sm truncate flex-1">{preview.file.original_filename}</span>
              <button type="button" onClick={() => { downloadFile(preview.file); }} data-testid="taseron-preview-download" className="inline-flex items-center gap-1.5 text-xs font-bold bg-emerald-600 text-white px-3 py-2 hover:bg-emerald-700 transition-colors duration-150 shrink-0"><Download size={13} /> İndir</button>
              <button type="button" onClick={() => setPreview(null)} data-testid="taseron-preview-close" className="text-slate-400 hover:text-slate-900 p-1 shrink-0"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center min-h-[320px]">
              {isImg(preview.file) ? (
                <img src={preview.url} alt={preview.file.original_filename} className="max-h-[78vh] max-w-full object-contain" />
              ) : isPdf(preview.file) ? (
                <iframe src={`${preview.url}#toolbar=0&navpanes=0&view=FitH`} title={preview.file.original_filename} className="w-full h-[78vh] border-0 bg-white" />
              ) : (
                <div className="text-center text-sm text-slate-500 py-16 px-6">
                  Bu dosya türü için önizleme desteklenmiyor.<br />
                  <button type="button" onClick={() => downloadFile(preview.file)} className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold bg-slate-900 text-white px-4 py-2.5 hover:bg-slate-700 transition-colors duration-150"><Download size={13} /> Dosyayı indir</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightbox(null)} data-testid="taseron-lightbox">
          <img src={lightbox.url} alt={lightbox.original_filename} onClick={(e) => e.stopPropagation()} className="max-h-[88vh] max-w-[92vw] object-contain select-none" />
          <button type="button" onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white/80 hover:text-white"><X size={26} /></button>
        </div>
      )}
    </div>
  );
}
